// Short two-note "ding-ding" chime, synthesized in-browser via the Web
// Audio API - no audio asset to bundle/host. Best-effort only: browsers can
// block audio before any user gesture, so failures are swallowed silently
// rather than surfacing an error for something this minor.
let audioCtx = null;

// A limiter in front of the speakers, so the chimes can be loud without
// the overlapping notes distorting.
function loudOutput(ctx) {
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -10;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.25;
  limiter.connect(ctx.destination);
  return limiter;
}

export function playNotificationTone() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    const out = loudOutput(audioCtx);
    [660, 880].forEach((freq, i) => {
      const start = now + i * 0.12;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.6, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
      osc.connect(gain).connect(out);
      osc.start(start);
      osc.stop(start + 0.3);
    });
  } catch (e) {
    // Silent - a missing/blocked chime should never break the page.
  }
}


// Emergency alarm for a new crisis alert: a rising-falling siren, about
// 2.5 seconds, clearly different from the message chime. Same best-effort
// rules - browsers only allow sound after the user has clicked the page once.
let currentAlarm = null;

// Stops the siren mid-way (the ribbon's mute button).
export function stopEmergencyAlarm() {
  try { if (currentAlarm) currentAlarm.stop(); } catch (e) { /* already stopped */ }
  currentAlarm = null;
}

export function playEmergencyAlarm() {
  stopEmergencyAlarm();
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "sawtooth";
    for (let i = 0; i < 3; i++) {
      osc.frequency.setValueAtTime(650, now + i * 0.8);
      osc.frequency.linearRampToValueAtTime(1150, now + i * 0.8 + 0.4);
      osc.frequency.linearRampToValueAtTime(650, now + i * 0.8 + 0.8);
    }
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.05);
    gain.gain.setValueAtTime(0.12, now + 2.3);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.45);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start(now);
    osc.stop(now + 2.5);
    currentAlarm = osc;
    osc.onended = () => { if (currentAlarm === osc) currentAlarm = null; };
  } catch (e) {
    // Silent - see playNotificationTone.
  }
}


// Loud, bright "station announcement" chime for a patient waiting in a call:
// a vibraphone-like rising four-note phrase, played twice. Our own melody in
// the style of a railway-station announcement chime (not a copy of any real
// one), synthesized here, so there's no audio file. Same best-effort rules.
export function playAnnouncementChime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const master = ctx.createGain();
    master.gain.value = 1.6; // the limiter keeps it clean
    master.connect(loudOutput(ctx));
    // A major arpeggio up, then the top note held: G4 B4 D5 G5.
    const phrase = [392.0, 493.88, 587.33, 783.99];
    const start = ctx.currentTime + 0.05;
    [0, 1.6].forEach((offset) => {
      phrase.forEach((freq, i) => {
        const t = start + offset + i * 0.28;
        const hold = i === phrase.length - 1 ? 1.1 : 0.55;
        // Stronger upper partials make it cut through more than a pure tone.
        [[freq, 1], [freq * 2, 0.45], [freq * 3, 0.2], [freq * 4, 0.08]].forEach(([f, level]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = f;
          gain.gain.setValueAtTime(0.0001, t);
          gain.gain.exponentialRampToValueAtTime(level, t + 0.012);
          gain.gain.exponentialRampToValueAtTime(0.0001, t + hold);
          osc.connect(gain).connect(master);
          osc.start(t);
          osc.stop(t + hold + 0.05);
        });
      });
    });
  } catch (e) {
    // Silent - a blocked chime should never break the page.
  }
}
